/**
 * BELISSIA — Varianten-Auswahlliste mit Bild je Option (2026-09-05).
 *
 * Der native <select> im Wrapper bleibt der Zustandstraeger: variant-picker.js hoert auf
 * sein `change`-Ereignis, markiert die gewaehlte <option> und laedt den Abschnitt neu
 * (Morph). Dieses Element ist nur die Bedienoberflaeche davor: ein Knopf mit der
 * gewaehlten Option und eine Liste (role="listbox") mit Bild und Namen je Option.
 *
 * Die Liste liegt in einem Popover (popover="manual") in der Top-Layer-Ebene des
 * Browsers: dort kann kein Stapelkontext einer folgenden Sektion (Produktkarten, Bilder)
 * sie verdecken, und kein overflow eines Vorfahren schneidet sie ab. Der Popover-Container
 * deckt den ganzen Viewport ab (Klick daneben schliesst, ohne die Seite darunter zu
 * treffen); das Panel darin wird am Desktop per JS unter (bei Platzmangel ueber) dem Feld
 * positioniert und zeigt hoechstens LIST_VISIBLE_ROWS Zeilen, danach scrollt es. Unter
 * 750 px Breite ist das Panel ein Bottom-Sheet (CSS), der Container abgedunkelt.
 * Browser ohne Popover-API fallen auf denselben Container als position:fixed zurueck.
 *
 * Alles, was der Morph nach einem Variantenwechsel ueberschreibt (aria-expanded, hidden,
 * aria-selected), wird bewusst NICHT als Zustand gehalten: die Liste ist nach einer Wahl
 * ohnehin geschlossen, und der frische Server-Stand ist die Wahrheit. Die Sichtbarkeit
 * des nativen Select regelt CSS ueber `variant-image-dropdown:defined`.
 */
const LIST_GAP_PX = 6;
const LIST_VISIBLE_ROWS = 5;
const LIST_ROW_FALLBACK_PX = 56;
const LIST_PADDING_PX = 12;
const LIST_MIN_HEIGHT_PX = 160;
const VIEWPORT_MARGIN_PX = 12;
const MOBILE_QUERY = '(max-width: 749px)';

class VariantImageDropdown extends HTMLElement {
  #bound = false;

  #supportsPopover = typeof HTMLElement.prototype.showPopover === 'function';

  #onDocumentPointerDown = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.close();
  };

  #onDocumentKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    event.preventDefault();
    this.close();
    this.toggle?.focus();
  };

  #onViewportChange = () => {
    if (!this.isOpen) return;
    if (this.#isMobile()) {
      this.#position();
      return;
    }
    const anchor = this.anchor;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      this.close();
      return;
    }
    this.#position();
  };

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const option = target.closest('.variant-dropdown__option');
    if (option instanceof HTMLElement && this.contains(option)) {
      event.preventDefault();
      this.#choose(option);
      return;
    }

    if (target.closest('.variant-dropdown__close') || target === this.popover) {
      event.preventDefault();
      this.close();
      this.toggle?.focus({ preventScroll: true });
      return;
    }

    if (target.closest('.variant-dropdown__toggle')) {
      event.preventDefault();
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }
  };

  #onKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('.variant-dropdown__toggle')) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.open();
      }
      return;
    }

    const option = target.closest('.variant-dropdown__option');
    if (!(option instanceof HTMLElement)) return;

    const options = this.options;
    const index = options.indexOf(option);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#naechsteWaehlbare(options, index, 1)?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#naechsteWaehlbare(options, index, -1)?.focus();
        break;
      case 'Home':
        event.preventDefault();
        this.#naechsteWaehlbare(options, -1, 1)?.focus();
        break;
      case 'End':
        event.preventDefault();
        this.#naechsteWaehlbare(options, options.length, -1)?.focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#choose(option);
        break;
      case 'Tab':
        this.close();
        break;
      default:
        break;
    }
  };

  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.#unbindDocument();
  }

  /** @returns {HTMLButtonElement | null} */
  get toggle() {
    return this.querySelector('.variant-dropdown__toggle');
  }

  /** Der Popover-Container (deckt den Viewport ab). */
  get popover() {
    return this.querySelector('.variant-dropdown__popover');
  }

  /** Das positionierte Panel im Container (am Desktop unter dem Feld, mobil das Sheet). */
  get panel() {
    return this.querySelector('.variant-dropdown__panel');
  }

  /** @returns {HTMLElement | null} */
  get list() {
    return this.querySelector('.variant-dropdown__list');
  }

  /** Das Feld, unter dem die Liste haengt (Wrapper mit Rahmen, sonst der Knopf). */
  get anchor() {
    return this.querySelector('.variant-option__select-wrapper') ?? this.toggle;
  }

  /** @returns {HTMLSelectElement | null} */
  get select() {
    return this.querySelector('select');
  }

  /** @returns {HTMLElement[]} */
  get options() {
    return Array.from(this.querySelectorAll('.variant-dropdown__option'));
  }

  /**
   * Gesperrt ist, was das Liquid als nicht waehlbar ausweist (`aria-disabled="true"`) --
   * dieselbe Aussage, die im nativen <select> als `disabled` steht. `data-unavailable`
   * taugt dafuer NICHT: es steuert die Anzeige im Knopf und stuende auch am gewaehlten,
   * ausverkauften Wert, den der Waehler weiterhin fuehren muss.
   * @param {HTMLElement | undefined} option
   */
  #istGesperrt(option) {
    return option?.getAttribute('aria-disabled') === 'true';
  }

  /**
   * Der naechste WAEHLBARE Eintrag in der gegebenen Richtung, sonst null. Ein gesperrter
   * Eintrag wird von den Pfeiltasten uebersprungen, statt den Fokus aufzunehmen und dort
   * ins Leere zu fuehren.
   * @param {HTMLElement[]} options
   * @param {number} von
   * @param {number} schritt
   * @returns {HTMLElement | null}
   */
  #naechsteWaehlbare(options, von, schritt) {
    for (let i = von + schritt; i >= 0 && i < options.length; i += schritt) {
      const kandidat = options[i];
      if (kandidat && !this.#istGesperrt(kandidat)) return kandidat;
    }
    return null;
  }

  get isOpen() {
    return this.toggle?.getAttribute('aria-expanded') === 'true';
  }

  #isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  open() {
    const toggle = this.toggle;
    const popover = this.popover;
    if (!toggle || !popover || this.isOpen) return;

    popover.hidden = false;
    if (this.#supportsPopover && popover.hasAttribute('popover') && !popover.matches(':popover-open')) {
      try {
        popover.showPopover();
      } catch {
        // z. B. wenn das Element gerade nicht verbunden ist: dann bleibt der Fallback ohne Top-Layer.
      }
    }
    this.#position();
    toggle.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, true);
    document.addEventListener('keydown', this.#onDocumentKeydown, true);
    window.addEventListener('scroll', this.#onViewportChange, { capture: true, passive: true });
    window.addEventListener('resize', this.#onViewportChange, { passive: true });

    const options = this.options;
    const current =
      options.find((item) => item.getAttribute('aria-selected') === 'true') ??
      this.#naechsteWaehlbare(options, -1, 1) ??
      options[0];
    current?.focus({ preventScroll: true });
    current?.scrollIntoView({ block: 'nearest' });
  }

  close() {
    this.#unbindDocument();
    const popover = this.popover;
    if (popover) {
      if (this.#supportsPopover && popover.matches(':popover-open')) {
        try {
          popover.hidePopover();
        } catch {
          // bereits geschlossen
        }
      }
      popover.hidden = true;
    }
    this.toggle?.setAttribute('aria-expanded', 'false');
  }

  /**
   * Desktop: Panel als position:fixed unter das Feld, Hoehe = LIST_VISIBLE_ROWS Zeilen
   * (danach scrollt die Liste); reicht der Platz nach unten nicht und ist oben mehr,
   * klappt es nach oben. Mobil: keine Inline-Werte, das Sheet kommt aus dem CSS.
   */
  #position() {
    const panel = this.panel;
    const list = this.list;
    const anchor = this.anchor;
    if (!panel || !list || !anchor) return;

    if (this.#isMobile()) {
      panel.removeAttribute('style');
      list.style.removeProperty('max-height');
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const firstRow = this.options[0];
    const rowHeight = firstRow ? firstRow.getBoundingClientRect().height || LIST_ROW_FALLBACK_PX : LIST_ROW_FALLBACK_PX;
    const wanted = Math.round(rowHeight * LIST_VISIBLE_ROWS + LIST_PADDING_PX);
    const spaceBelow = window.innerHeight - rect.bottom - LIST_GAP_PX - VIEWPORT_MARGIN_PX;
    const spaceAbove = rect.top - LIST_GAP_PX - VIEWPORT_MARGIN_PX;
    const openUpwards = spaceBelow < Math.min(wanted, LIST_MIN_HEIGHT_PX) && spaceAbove > spaceBelow;
    const available = Math.max(openUpwards ? spaceAbove : spaceBelow, 96);
    const maxHeight = Math.min(wanted, available);

    panel.style.position = 'fixed';
    panel.style.inset = 'auto';
    panel.style.margin = '0';
    panel.style.left = Math.round(rect.left) + 'px';
    panel.style.width = Math.round(rect.width) + 'px';
    list.style.maxHeight = Math.round(maxHeight) + 'px';
    if (openUpwards) {
      panel.style.top = 'auto';
      panel.style.bottom = Math.round(window.innerHeight - rect.top + LIST_GAP_PX) + 'px';
    } else {
      panel.style.bottom = 'auto';
      panel.style.top = Math.round(rect.bottom + LIST_GAP_PX) + 'px';
    }
  }

  #unbindDocument() {
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown, true);
    document.removeEventListener('keydown', this.#onDocumentKeydown, true);
    window.removeEventListener('scroll', this.#onViewportChange, { capture: true });
    window.removeEventListener('resize', this.#onViewportChange);
  }

  /**
   * Uebernimmt die Wahl in den nativen Select und meldet sie als `change`,
   * damit variant-picker.js den Variantenwechsel wie bisher ausfuehrt.
   * @param {HTMLElement} option
   */
  #choose(option) {
    // Ein gesperrter Eintrag ist kein Ziel -- weder per Klick noch per Enter. Ohne diesen
    // Riegel fuehrte ein Klick auf "Nicht verfuegbar" in eine tote Kombination: gemessen am
    // 09.09.2026 am Vorschau-Theme wechselte der Geschmack auf das gesperrte Set, der
    // Kaufknopf stand auf "Nicht verfuegbar" und es gab keinen Preis. Die Liste bleibt dabei
    // offen; ein Schliessen waere von einer angenommenen Wahl nicht zu unterscheiden.
    if (this.#istGesperrt(option)) return;

    const value = option.dataset.value ?? '';
    const select = this.select;

    this.#reflectChoice(option);
    this.close();
    this.toggle?.focus({ preventScroll: true });

    if (!select || select.value === value) return;
    const match = Array.from(select.options).find((item) => item.value === value);
    if (!match) return;

    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Sofortige Rueckmeldung im Knopf, bis der Morph den Server-Stand bringt.
   * @param {HTMLElement} option
   */
  #reflectChoice(option) {
    for (const item of this.options) {
      item.setAttribute('aria-selected', item === option ? 'true' : 'false');
    }

    const label = option.querySelector('.variant-dropdown__option-label')?.textContent?.trim();
    const cardValue = this.querySelector('.variant-option__select-card-value');
    if (label && cardValue) cardValue.textContent = label;

    const cardState = this.querySelector('.variant-option__select-card-state');
    if (cardState) cardState.hidden = option.dataset.unavailable !== 'true';

    const image = option.querySelector('img.variant-dropdown__option-image');
    const cardImage = this.querySelector('img.variant-option__select-card-image');
    if (image instanceof HTMLImageElement && cardImage instanceof HTMLImageElement && image.currentSrc) {
      cardImage.removeAttribute('srcset');
      cardImage.src = image.currentSrc;
    }
  }
}

if (!customElements.get('variant-image-dropdown')) {
  customElements.define('variant-image-dropdown', VariantImageDropdown);
}
